import React from "react";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  MAX_PAGE_SIZE,
  MIN_PAGE_SIZE,
} from "@common/components/GlobalPreferences/defaults";
import { useDebounce, useDebouncedState } from "@common/hooks";

interface PageSizeFieldProps {
  label: string;
  pageSize: number;
  setPageSize: (value: number) => void;
}

export const GlobalListPageSizeField = ({
  label,
  pageSize,
  setPageSize,
}: PageSizeFieldProps) => {
  const { t } = useTranslation();

  const applyChanges = useDebounce(() => !isNaN(value) && setPageSize(value));
  const handleChange = (v: string) => setValue(parseInt(v));

  const [value, setValue] = useDebouncedState(pageSize, (v) => {
    let newVal = v;

    if (newVal < MIN_PAGE_SIZE) {
      newVal = MIN_PAGE_SIZE;
    } else if (newVal > MAX_PAGE_SIZE) {
      newVal = MAX_PAGE_SIZE;
    }

    if (newVal !== value) {
      setValue(newVal);
    }

    if (newVal === value && newVal !== pageSize) {
      applyChanges();
    }
  });

  return (
    <FormControl component="fieldset">
      <FormLabel component="legend">{label}</FormLabel>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 1,
          mt: 1.5,
          mx: 3
        }}>
        <Tooltip
          arrow
          title={t(
            "Adjusting the number of rows displayed per page can impact performance, especially at higher values. For the best experience, choose a setting that suits your needs."
          )}
        >
          <TextField
            fullWidth
            value={isNaN(value) ? "" : value}
            onChange={(evt) => {
              handleChange(evt.target.value.replace(/[^0-9]/gi, ""));
            }}
            slotProps={{
              htmlInput: {
                inputMode: "numeric",
                pattern: "[0-9]*",
              }
            }}
          />
        </Tooltip>
      </Stack>
    </FormControl>
  );
};
